/**
 * Host-services factory (editor-core).
 *
 * Builds the concrete `HostServices` bundle the extension host needs to
 * activate extensions inside the running app:
 *
 *  - `RuntimeBridge` — routes UI `callRuntime`/`onRuntimeEvent` to the Engine
 *    via `extension/invoke` + `event/extension.<id>.<event>` broadcasts.
 *  - `EditorHostAPI` — bridges the editor shell (active document, cursor,
 *    selection, panels, commands, notifications) for extension UIs.
 *  - `HostSettingsServices` — per-extension workspace settings.
 *  - `HostLogger` — funnels extension logs into the unified log store.
 *
 * The i18n services are supplied by the React bootstrap (which owns locale
 * state); everything else here is framework-agnostic.
 */
import type {
    AnyExtensionContract,
    EditorHostAPI,
    ExtensionEventName,
    ExtensionEventPayload,
    ExtensionMethodName,
    ExtensionMethodParams,
    ExtensionMethodResult,
    Range,
} from "@magic-md/extension-sdk";

import { getPlatformService } from "../platform";
import { getNotificationBus } from "../store/notification-bus";
import { useEditorStore } from "../store/editor-store";
import { useLogStore } from "../store/log-store";
import { tabSessionRegistry } from "../store/tab-session-registry";
import { executeCommand } from "../lib/commands";
import type { FileTreeNode } from "../store/editor/types";

import { useExtensionHostStore } from "./extension-host-store";
import type {
    HostLogger,
    HostSettingsServices,
    RuntimeBridge,
} from "./host-services";

/* ── Runtime bridge ─────────────────────────────────────────────── */

/**
 * Build the `RuntimeBridge` backed by the Engine client + notification bus.
 * `extension/invoke` returns `{ success, data?, message? }`; we unwrap `data`
 * on success and throw on failure so UI `callRuntime` rejects cleanly.
 */
export function createRuntimeBridge(): RuntimeBridge {
    const engine = () => getPlatformService().engine;

    return {
        async invoke<C extends AnyExtensionContract, K extends ExtensionMethodName<C>>(
            extensionId: string,
            method: K,
            params: ExtensionMethodParams<C, K>,
        ): Promise<ExtensionMethodResult<C, K>> {
            const fn = engine().extensionInvoke;
            if (!fn) {
                throw new Error(
                    `[extension] runtime invoke unavailable (engine not connected): ${extensionId}.${String(method)}`,
                );
            }
            const res = await fn({
                extensionId,
                method: String(method),
                params: params as Record<string, unknown>,
            });
            if (!res.success) {
                throw new Error(
                    res.message ??
                        `[extension] runtime invoke failed: ${extensionId}.${String(method)}`,
                );
            }
            return res.data as ExtensionMethodResult<C, K>;
        },

        subscribe<C extends AnyExtensionContract, K extends ExtensionEventName<C>>(
            extensionId: string,
            event: K,
            handler: (data: ExtensionEventPayload<C, K>) => void,
        ): () => void {
            const method = `event/extension.${extensionId}.${String(event)}`;
            return getNotificationBus().subscribe<{
                extensionId: string;
                event: string;
                payload: unknown;
            }>(method, (params) => {
                handler(params?.payload as ExtensionEventPayload<C, K>);
            });
        },

        async ensureActivated(
            extensionId: string,
            settings: Readonly<Record<string, unknown>>,
        ): Promise<void> {
            const fn = engine().extensionActivateRuntime;
            if (!fn) return;
            await fn({ extensionId, settings: settings as Record<string, unknown> });
        },

        async dispose(extensionId: string): Promise<void> {
            const fn = engine().extensionDisposeRuntime;
            if (!fn) return;
            await fn({ extensionId });
        },

        async updateSettings(
            extensionId: string,
            settings: Readonly<Record<string, unknown>>,
        ): Promise<void> {
            useExtensionHostStore
                .getState()
                .setSettings(extensionId, settings as Record<string, unknown>);
            const fn = engine().extensionSettingsUpdate;
            if (!fn) return;
            await fn({ extensionId, settings: settings as Record<string, unknown> });
        },
    };
}

/* ── Editor host API ────────────────────────────────────────────── */

function activeTab() {
    const s = useEditorStore.getState();
    return s.tabs.find((t) => t.id === s.activeTabId);
}

const EXTENSION_WORKSPACE_FILE_LIMIT = 500;
const EXTENSION_WORKSPACE_DIR_LIMIT = 1500;
const EXTENSION_WORKSPACE_FILE_CACHE_TTL_MS = 10_000;
const EXTENSION_WORKSPACE_SKIP_DIRS = new Set([
    ".git",
    ".hg",
    ".svn",
    ".next",
    ".nuxt",
    ".turbo",
    ".vite",
    ".magic-md",
    "build",
    "coverage",
    "dist",
    "node_modules",
    "target",
]);
let workspaceFileCache:
    | { rootsKey: string; expiresAt: number; files: string[] }
    | null = null;

function workspaceFileSearchTerm(glob?: string): string {
    if (!glob || glob === "**/*") return "";
    return glob
        .replace(/\*\*\/\*/g, "")
        .replace(/\*/g, "")
        .trim()
        .toLowerCase();
}

function labelFromPath(path: string): string {
    return path.split("/").filter(Boolean).pop() ?? path;
}

function matchesWorkspaceFileSearch(path: string, name: string, term: string): boolean {
    if (!term) return true;
    const normalized = term.replace(/^\/+|\/+$/g, "");
    if (!normalized) return true;
    return path.toLowerCase().includes(normalized) || name.toLowerCase().includes(normalized);
}

function filterWorkspaceFiles(paths: string[], term: string): string[] {
    return paths.filter((path) => matchesWorkspaceFileSearch(path, labelFromPath(path), term));
}

function collectWorkspaceFilesFromTree(
    nodes: readonly FileTreeNode[],
    term: string,
    files: string[] = [],
    seen: Set<string> = new Set(),
): string[] {
    for (const node of nodes) {
        if (files.length >= EXTENSION_WORKSPACE_FILE_LIMIT) break;
        if (node.isDirectory) {
            if (EXTENSION_WORKSPACE_SKIP_DIRS.has(node.name)) continue;
            if (node.children?.length) {
                collectWorkspaceFilesFromTree(node.children, term, files, seen);
            }
            continue;
        }
        if (seen.has(node.path)) continue;
        if (!matchesWorkspaceFileSearch(node.path, node.name, term)) continue;
        seen.add(node.path);
        files.push(node.path);
    }
    return files;
}

async function scanWorkspaceFilesFromEngine(
    roots: string[],
    readDir: ReturnType<typeof getPlatformService>["engine"]["workspaceReadDir"],
): Promise<string[]> {
    const files: string[] = [];
    const seenFiles = new Set<string>();
    const seenDirs = new Set<string>();
    const queue = [...roots];
    let scannedDirs = 0;

    while (
        queue.length > 0 &&
        files.length < EXTENSION_WORKSPACE_FILE_LIMIT &&
        scannedDirs < EXTENSION_WORKSPACE_DIR_LIMIT
    ) {
        const dir = queue.shift();
        if (!dir || seenDirs.has(dir)) continue;
        seenDirs.add(dir);
        scannedDirs += 1;

        const entries = (await readDir({ path: dir, allowMissing: true })
            .catch(() => ({ entries: [] }))).entries;
        const sorted = [...entries].sort((a, b) => {
            if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
            return a.name.localeCompare(b.name);
        });

        for (const entry of sorted) {
            if (entry.isDirectory) {
                if (entry.isSymlink || EXTENSION_WORKSPACE_SKIP_DIRS.has(entry.name)) {
                    continue;
                }
                queue.push(entry.path);
                continue;
            }
            if (!entry.isFile || seenFiles.has(entry.path)) continue;
            seenFiles.add(entry.path);
            files.push(entry.path);
            if (files.length >= EXTENSION_WORKSPACE_FILE_LIMIT) break;
        }
    }
    return files;
}

/**
 * Build the `EditorHostAPI` bridging the editor shell. Document/cursor/panel
 * operations are wired to real stores; document-mutation helpers that require
 * the unified tool-registry fall back to best-effort content edits.
 */
export function createEditorHostApi(): EditorHostAPI {
    const engine = () => getPlatformService().engine;

    function insertAtCursor(text: string): void {
        const store = useEditorStore.getState();
        const tab = activeTab();
        if (!tab) return;
        // Best-effort: append at end when no precise offset mapping is available.
        store.setContent(`${tab.content}${text}`, tab.id);
    }

    return {
        getActiveDocument() {
            const tab = activeTab();
            if (!tab) return null;
            return {
                path: tab.filePath,
                sessionId: tabSessionRegistry.getSessionForTab(tab.id),
                content: tab.content,
            };
        },

        getOpenDocuments() {
            const state = useEditorStore.getState();
            const docs = state.tabs
                .filter((tab) => !!tab.filePath && !tab.isLoading)
                .map((tab) => ({
                    path: tab.filePath,
                    sessionId: tabSessionRegistry.getSessionForTab(tab.id),
                    title: tab.title,
                    content: tab.content,
                    active: tab.id === state.activeTabId,
                }));
            return docs
                .sort((a, b) => Number(b.active) - Number(a.active))
                .map(({ active: _active, ...doc }) => doc);
        },

        insertAtCursor,

        replaceRange(range: Range, text: string) {
            const store = useEditorStore.getState();
            const tab = activeTab();
            if (!tab) return;
            const next =
                tab.content.slice(0, range.from) + text + tab.content.slice(range.to);
            store.setContent(next, tab.id);
        },

        createDefinition() {
            // Routed through the unified tool-registry in full builds; no-op here.
        },
        editDefinition() {
            // Routed through the unified tool-registry in full builds; no-op here.
        },

        async runDefinition() {
            // Definition execution requires a resolved unit context; no-op here.
        },

        async getPluginSchema() {
            // Plugin schema is sourced from the manifest registry in full builds.
            return {};
        },

        async listPlugins() {
            const res = await engine().pluginList();
            return (res.plugins ?? []).map((p) => ({ name: p.name, title: p.title }));
        },

        async listWorkspaceFiles(glob?: string) {
            const store = useEditorStore.getState();
            let roots = store.workspaceFolders.map((folder) => folder.path);
            if (roots.length === 0) {
                roots = (await engine().workspaceListFolders().catch(() => ({ folders: [] })))
                    .folders.map((folder) => folder.path);
            }
            const term = workspaceFileSearchTerm(glob);
            const treeFiles = collectWorkspaceFilesFromTree(store.fileTree, term);
            if (treeFiles.length > 0) return treeFiles;

            const rootsKey = roots.join("\u0000");
            const now = Date.now();
            if (
                workspaceFileCache &&
                workspaceFileCache.rootsKey === rootsKey &&
                workspaceFileCache.expiresAt > now
            ) {
                return filterWorkspaceFiles(workspaceFileCache.files, term)
                    .slice(0, EXTENSION_WORKSPACE_FILE_LIMIT);
            }

            const engineService = engine();
            const files = await scanWorkspaceFilesFromEngine(
                roots,
                (params) => engineService.workspaceReadDir(params),
            );
            workspaceFileCache = {
                rootsKey,
                expiresAt: now + EXTENSION_WORKSPACE_FILE_CACHE_TTL_MS,
                files,
            };
            return filterWorkspaceFiles(files, term).slice(0, EXTENSION_WORKSPACE_FILE_LIMIT);
        },

        async openFile(path: string) {
            const store = useEditorStore.getState() as unknown as {
                openFileFromPath?: (p: string) => Promise<void> | void;
            };
            await store.openFileFromPath?.(path);
        },

        async readFile(path: string) {
            const openTab = useEditorStore
                .getState()
                .tabs.find((tab) => tab.filePath === path && !tab.isLoading);
            if (openTab) return openTab.content;
            return (await engine().workspaceReadFile({ path })).content;
        },

        async executeCommand(id: string) {
            await executeCommand(id);
            return undefined;
        },

        focusPanel(panelId: string) {
            useExtensionHostStore.getState().focusPanel(panelId);
        },

        getSelection() {
            return null;
        },

        async promptInput(message: string) {
            if (typeof window !== "undefined" && typeof window.prompt === "function") {
                return window.prompt(message);
            }
            return null;
        },

        showNotification(opts) {
            const level = opts.level === "warn" ? "warn" : opts.level === "error" ? "error" : "info";
            useLogStore.getState().log(level, opts.message, { source: "extension" });
            useEditorStore.getState().setStatusMessage(opts.message);
        },
    };
}

/* ── Settings + logging ─────────────────────────────────────────── */

/** Per-extension workspace settings backed by the extension-host store. */
export function createHostSettingsServices(): HostSettingsServices {
    return {
        get(extensionId: string) {
            return useExtensionHostStore.getState().getSettings(extensionId);
        },
        onChanged(extensionId: string, cb: (next: Record<string, unknown>) => void) {
            let prev = useExtensionHostStore.getState().getSettings(extensionId);
            return useExtensionHostStore.subscribe((state) => {
                const next = state.getSettings(extensionId);
                if (next !== prev) {
                    prev = next;
                    cb(next);
                }
            });
        },
    };
}

/** Funnel extension logs into the unified log store under the `extension` source. */
export const hostLogger: HostLogger = (extensionId, level, msg) => {
    useLogStore.getState().log(level, `[${extensionId}] ${msg}`, { source: "extension" });
};
